import { formatDistanceToNow } from 'date-fns';
import type { Capture, CaptureType } from '@/types';

import styles from './InboxContent.module.css';

interface InboxContentProps {
  captures: Capture[];
  onConvertToTask: (capture: Capture) => void;
  onConvertToBlock: (capture: Capture) => void;
  onArchive: (captureId: string) => void;
  onDelete: (captureId: string) => void;
}

const TYPE_ICONS: Record<CaptureType, string> = {
  thought: '\u{1F4AD}', // speech bubble
  task: '\u{2705}', // check mark
  tomorrow: '\u{1F4C5}', // calendar
  soon: '\u{1F51C}', // soon arrow
  note: '\u{1F4DD}', // memo
  'quick-block': '\u{26A1}', // lightning bolt
};

const TYPE_LABELS: Record<CaptureType, string> = {
  thought: 'Thought',
  task: 'Task',
  tomorrow: 'Tomorrow',
  soon: 'Soon',
  note: 'Note',
  'quick-block': 'Quick Block',
};

function formatTimestamp(date: Date): string {
  return formatDistanceToNow(date, { addSuffix: true });
}

export function InboxContent({
  captures,
  onConvertToTask,
  onConvertToBlock,
  onArchive,
  onDelete,
}: InboxContentProps) {
  const inboxItems = captures.filter((c) => c.status === 'inbox');

  if (inboxItems.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true">
            {'\u{1F4E5}'}
          </div>
          <p className={styles.emptyTitle}>Inbox empty</p>
          <p className={styles.emptySubtitle}>Captured thoughts will appear here for processing</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.countBadge}>{inboxItems.length}</span>
        <span className={styles.headerText}>items to process</span>
      </div>

      <ul className={styles.list} role="list">
        {inboxItems.map((capture) => (
          <li key={capture.id} className={styles.item}>
            <div className={styles.itemHeader}>
              <span
                className={styles.typeIcon}
                title={TYPE_LABELS[capture.type]}
                aria-label={TYPE_LABELS[capture.type]}
              >
                {TYPE_ICONS[capture.type]}
              </span>
              <span className={styles.typeLabel}>{TYPE_LABELS[capture.type]}</span>
              <span className={styles.timestamp}>{formatTimestamp(capture.createdAt)}</span>
            </div>

            <p className={styles.content}>{capture.content}</p>

            {capture.tags && capture.tags.length > 0 && (
              <div className={styles.tags}>
                {capture.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => onConvertToTask(capture)}
                title="Convert to Task"
                aria-label="Convert to Task"
              >
                <span aria-hidden="true">{'\u{2192}'}</span>
                <span aria-hidden="true">{'\u{1F4CB}'}</span>
              </button>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => onConvertToBlock(capture)}
                title="Convert to Block"
                aria-label="Convert to Block"
              >
                <span aria-hidden="true">{'\u{2192}'}</span>
                <span aria-hidden="true">{'\u{1F4C5}'}</span>
              </button>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.archiveBtn}`}
                onClick={() => onArchive(capture.id)}
                title="Archive"
                aria-label="Archive"
              >
                <span aria-hidden="true">{'\u{1F4E6}'}</span>
              </button>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.deleteBtn}`}
                onClick={() => onDelete(capture.id)}
                title="Delete"
                aria-label="Delete"
              >
                <span aria-hidden="true">{'\u{1F5D1}'}</span>
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default InboxContent;
